// src/components/WorkshopDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// Import MUI
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

// Card per l'Ordine di Lavoro
const WorkOrderCard = ({ order, t }) => {
  const navigate = useNavigate();
  
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card 
        elevation={0} 
        sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          height: '100%',
          backgroundColor: 'background.paper'
        }}
      >
        <CardActionArea 
          onClick={() => navigate(`/workshop/${order.id}`)} 
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
        >
          <CardContent sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {order.title}
              </Typography>
              <Chip
                icon={order.status === 'Completed' ? <CheckCircleIcon /> : <PendingIcon />}
                label={order.status}
                color={order.status === 'Completed' ? 'success' : 'warning'}
                size="small"
              />
            </Box>
            <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, mb: 2 }}>
              {t('card.vehicle')} {order.vehicleName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              {t('card.created')} {order.createdAt ? format(order.createdAt.toDate(), 'dd/MM/yyyy') : 'N/A'}
            </Typography>
            {order.status === 'Completed' && (
              <Typography variant="body2" color="text.secondary">
                {t('card.completed')} {order.completedAt ? format(order.completedAt.toDate(), 'dd/MM/yyyy') : 'N/A'}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {order.itemCount || 0} {t('card.tasks')}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};


function WorkshopDashboard({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('workshop');
  // --- FINE MODIFICA ---

  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState('Open');

  const isManager = userRole === 'admin' || userRole === 'Engineer';

  // Carica gli ordini di lavoro
  useEffect(() => {
    if (!teamId) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    const q = query(
      collection(db, "workOrders"),
      where("teamId", "==", teamId),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setWorkOrders(ordersList);
      setIsLoading(false);
    }, (err) => {
      console.error("Error fetching work orders:", err);
      setError(t('load_error')); // Tradotto
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [teamId, t]); // Aggiunto t

  // Filtra gli ordini per tab
  const filteredOrders = useMemo(() => {
    return workOrders.filter(order => order.status === currentTab);
  }, [workOrders, currentTab]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
      {/* 1. Header e Pulsante Azione */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {t('title')} {/* Tradotto */}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')} {/* Tradotto */}
          </Typography>
        </Box>
        {isManager && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/workshop/new')}
            sx={{ height: '56px' }}
          >
            {t('new_order_button')} {/* Tradotto */}
          </Button>
        )}
      </Paper>

      {/* 2. Tabs e Griglia Ordini */}
      <Paper 
        elevation={0} 
        sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          backgroundColor: 'background.paper' 
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)}>
            <Tab label={t('tabs.open')} value="Open" icon={<PendingIcon />} iconPosition="start" />
            <Tab label={t('tabs.completed')} value="Completed" icon={<CheckCircleIcon />} iconPosition="start" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {filteredOrders.length === 0 && (
            <Alert severity="info" sx={{ m: 2 }}>
              {currentTab === 'Open' ? t('no_orders_open') : t('no_orders_completed')} {/* Tradotto */}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            {filteredOrders.map(order => (
              <WorkOrderCard key={order.id} order={order} t={t} />
            ))}
          </Grid>
        </Box>
      </Paper>
    </>
  );
}

export default WorkshopDashboard;